// ---- Elo forecast -------------------------------------------------------
// Pure math + a full prefilled-bracket generator. No React, no DOM.

import {
  GROUPS,
  GROUP_KEYS,
  ELO,
  R32_MATCHES,
  R16_PAIRS,
  QF_PAIRS,
  SF_PAIRS,
  F_PAIRS,
  R16_BASE,
  QF_BASE,
  SF_BASE,
  F_BASE
} from "./data.js";
import { assignThirds, resolveSlot, buildRound } from "./bracket.js";

// Elo expected score (win prob for A vs B), standard 400-scale logistic.
export const expScore = (a, b) => 1 / (1 + Math.pow(10, (b - a) / 400));

// Expected league points for one match. Models a draw bump that's largest
// when teams are evenly matched, so close games share points realistically.
export const matchPoints = (ra, rb) => {
  const e = expScore(ra, rb);
  const pDraw = 0.27 * Math.exp(-Math.pow((e - 0.5) / 0.28, 2) / 2);
  const pWinA = Math.max(0, e - pDraw / 2);
  const pWinB = Math.max(0, 1 - e - pDraw / 2);
  return { a: 3 * pWinA + pDraw, b: 3 * pWinB + pDraw };
};

// Monte Carlo a group N times to estimate each team's chance of finishing
// 1st, 2nd, or 3rd (the three positions that can lead to the knockouts).
export const simulateGroupProbs = (teams, runs = 4000) => {
  const r = teams.map(t => ELO[t] ?? 1500);
  const finish = teams.map(() => ({ first: 0, second: 0, third: 0 }));
  const pairs = [];
  for (let i = 0; i < teams.length; i++)
    for (let j = i + 1; j < teams.length; j++) pairs.push([i, j]);
  for (let run = 0; run < runs; run++) {
    const pts = teams.map(() => 0);
    const gd = teams.map(() => 0);
    for (const [i, j] of pairs) {
      const e = expScore(r[i], r[j]);
      const pDraw = 0.27 * Math.exp(-Math.pow((e - 0.5) / 0.28, 2) / 2);
      const x = Math.random();
      if (x < e - pDraw / 2) {
        pts[i] += 3;
        gd[i] += 1;
        gd[j] -= 1;
      } else if (x < e + pDraw / 2) {
        pts[i] += 1;
        pts[j] += 1;
      } else {
        pts[j] += 3;
        gd[j] += 1;
        gd[i] -= 1;
      }
    }
    const order = teams
      .map((_, idx) => idx)
      .sort((a, b) => pts[b] - pts[a] || gd[b] - gd[a] || r[b] - r[a]);
    finish[order[0]].first++;
    finish[order[1]].second++;
    finish[order[2]].third++;
  }
  const out = {};
  teams.forEach((t, idx) => {
    out[t] = {
      first: finish[idx].first / runs,
      second: finish[idx].second / runs,
      third: finish[idx].third / runs,
      advanceTop2: (finish[idx].first + finish[idx].second) / runs
    };
  });
  return out;
};

// Deterministic expected-points ranking of a group (no randomness).
export const forecastGroup = teams => {
  const pts = Object.fromEntries(teams.map(t => [t, 0]));
  for (let i = 0; i < teams.length; i++) {
    for (let j = i + 1; j < teams.length; j++) {
      const A = teams[i];
      const B = teams[j];
      const mp = matchPoints(ELO[A] ?? 1500, ELO[B] ?? 1500);
      pts[A] += mp.a;
      pts[B] += mp.b;
    }
  }
  return [...teams]
    .sort((x, y) => pts[y] - pts[x] || (ELO[y] ?? 1500) - (ELO[x] ?? 1500))
    .map(team => ({ team, xPts: pts[team] }));
};

// Returns a complete prefilled bracket: group placements, the 8 advancing
// thirds, and every knockout winner picked by higher Elo.
export function computeEloForecast() {
  const newResults = {};
  const thirdInfo = [];
  for (const g of GROUP_KEYS) {
    const ranked = forecastGroup(GROUPS[g]);
    newResults[g] = { first: ranked[0].team, second: ranked[1].team, third: ranked[2].team };
    thirdInfo.push({ g, xPts: ranked[2].xPts, elo: ELO[ranked[2].team] ?? 1500 });
  }
  const bestThirds = [...thirdInfo]
    .sort((a, b) => b.xPts - a.xPts || b.elo - a.elo)
    .slice(0, 8)
    .map(x => x.g)
    .sort((a, b) => GROUP_KEYS.indexOf(a) - GROUP_KEYS.indexOf(b));
  const assignment = assignThirds(bestThirds) || {};

  const newWinners = {};
  const higher = (a, b) =>
    !a ? b : !b ? a : (ELO[a] ?? 1500) >= (ELO[b] ?? 1500) ? a : b;

  const r32r = R32_MATCHES.map(m => ({
    id: m.id,
    home: resolveSlot(m.home, m.id, assignment, newResults),
    away: resolveSlot(m.away, m.id, assignment, newResults)
  }));
  r32r.forEach(m => {
    newWinners[m.id] = higher(m.home, m.away);
  });

  const advance = (prev, pairs, baseId) =>
    buildRound(prev, pairs, baseId).map(m => {
      newWinners[m.id] = higher(m.home, m.away);
      return newWinners[m.id];
    });

  const r32W = r32r.map(m => newWinners[m.id]);
  const r16W = advance(r32W, R16_PAIRS, R16_BASE);
  const qfW = advance(r16W, QF_PAIRS, QF_BASE);
  const sfW = advance(qfW, SF_PAIRS, SF_BASE);
  advance(sfW, F_PAIRS, F_BASE);

  return { groupResults: newResults, thirdsAdvancing: bestThirds, winners: newWinners };
}
